// src/services/pdf/sections/FlagStripSection.ts
// Renders the auto-generated clinical signal flags (red/amber/green strip).

import { PDFDocument } from "../core/PDFDocument";
import { PDF_COLORS } from "../core/colors";
import { PAGE_LAYOUT } from "../core/layout";
import type { PDFRenderData, ClinicalFlag, FlagLevel } from "../types";
import type { SectionRenderer } from "./SectionRenderer";
import type { RGBColor } from "../core/colors";

const FLAG_COLORS: Record<FlagLevel, { bg: RGBColor; text: RGBColor; dot: RGBColor }> = {
  red: { bg: PDF_COLORS.bgRedTint, text: PDF_COLORS.red, dot: PDF_COLORS.red },
  amber: { bg: PDF_COLORS.bgAmberTint, text: PDF_COLORS.amber, dot: PDF_COLORS.amber },
  green: { bg: PDF_COLORS.bgGreenTint, text: PDF_COLORS.green, dot: PDF_COLORS.green },
};

export class FlagStripSection implements SectionRenderer {
  readonly id = "flagStrip";

  render(doc: PDFDocument, data: PDFRenderData): void {
    const { flags } = data;
    if (flags.length === 0) return;

    doc.ensureSpace(12);

    // Section title
    doc.setFont("bold", PAGE_LAYOUT.fontSize.subsectionTitle);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.text("CLINICAL SIGNALS", doc.contentLeft);
    doc.advanceY(5);

    // Render flags in a compact strip
    for (const flag of flags) {
      this.renderFlag(doc, flag);
    }

    doc.advanceY(PAGE_LAYOUT.subsectionGap);
    doc.drawHorizontalRule();
  }

  private renderFlag(doc: PDFDocument, flag: ClinicalFlag): void {
    const colors = FLAG_COLORS[flag.level];
    const startY = doc.y;
    const rowHeight = 6;

    doc.ensureSpace(rowHeight + 2);

    // Background stripe
    doc.drawRect(
      doc.contentLeft, startY - 1,
      doc.contentWidth, rowHeight,
      { fill: colors.bg, radius: 1.5 }
    );

    // Vertically center text within the rectangle
    const textY = startY + rowHeight / 2 - 0.5;
    doc.y = textY;

    // Dot indicator
    doc.drawCircle(doc.contentLeft + 3, textY, 1.2, colors.dot);

    // Flag text
    doc.setFont("bold", PAGE_LAYOUT.fontSize.small);
    doc.setColor(colors.text);
    doc.text(flag.label, doc.contentLeft + 7);

    // Detail text
    if (flag.detail) {
      doc.setFont("normal", PAGE_LAYOUT.fontSize.small);
      doc.setColor(PDF_COLORS.textSecondary);
      const detailX = doc.contentLeft + 7 + doc.getTextWidth(flag.label) + 4;
      const maxDetailWidth = doc.contentRight - detailX - 2;
      if (maxDetailWidth > 20) {
        doc.text(`- ${flag.detail}`, detailX, { maxWidth: maxDetailWidth });
      }
    }

    doc.y = startY;
    doc.advanceY(rowHeight + 1.5);
  }
}
